<script setup>
/**
 * The identity card at the top of a single app's page.
 *
 * One row in a kit card: the app's mark, its name with a chip saying whether
 * the workspace is wired to it, the blurb, and the one action that matters
 * next - connect it or disconnect it. The `action` slot is how
 * apps that need their own connect flow (Notion's OAuth, Shopify's store-URL
 * dialog) replace the default link.
 */
import { ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import SettingsStatusChip from 'dashboard/components-next/Settings/SettingsStatusChip.vue';

const props = defineProps({
  integrationId: {
    type: [String, Number],
    required: true,
  },
  integrationName: { type: String, default: '' },
  integrationDescription: { type: String, default: '' },
  integrationEnabled: { type: Boolean, default: false },
  integrationAction: { type: String, default: '' },
  actionButtonText: { type: String, default: '' },
  deleteConfirmationText: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { replaceInstallationName } = useBranding();

const dialogRef = ref(null);

const openDeletePopup = () => {
  if (dialogRef.value) {
    dialogRef.value.open();
  }
};

const closeDeletePopup = () => {
  if (dialogRef.value) {
    dialogRef.value.close();
  }
};

const deleteIntegration = async () => {
  try {
    await store.dispatch('integrations/deleteIntegration', props.integrationId);
    useAlert(t('INTEGRATION_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.API.ERROR_MESSAGE'));
  }
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteIntegration();
  router.push({ name: 'settings_applications' });
};
</script>

<template>
  <SettingsSection>
    <div class="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center">
      <div class="flex items-center justify-center flex-shrink-0 size-10">
        <img
          :src="`/dashboard/images/integrations/${integrationId}.png`"
          class="block max-w-full border rounded-md shadow-sm dark:hidden border-n-weak bg-n-alpha-3 dark:bg-n-alpha-2"
        />
        <img
          :src="`/dashboard/images/integrations/${integrationId}-dark.png`"
          class="hidden max-w-full border rounded-md shadow-sm dark:block border-n-weak bg-n-alpha-3 dark:bg-n-alpha-2"
        />
      </div>

      <div class="flex flex-col flex-1 min-w-0 gap-1">
        <div class="flex items-center gap-2 min-w-0">
          <h3 class="text-sm font-medium truncate text-n-slate-12">
            {{ integrationName }}
          </h3>
          <SettingsStatusChip
            :label="
              integrationEnabled
                ? $t('SETTINGS_KIT.CONNECTED')
                : $t('SETTINGS_KIT.READY_TO_USE')
            "
            :tone="integrationEnabled ? 'success' : 'neutral'"
          />
        </div>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ replaceInstallationName(integrationDescription) }}
        </p>
      </div>

      <div class="flex items-center flex-shrink-0">
        <Button
          v-if="integrationEnabled && integrationAction === 'disconnect'"
          size="sm"
          variant="faded"
          color="ruby"
          :label="
            actionButtonText ||
            $t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.BUTTON_TEXT')
          "
          @click="openDeletePopup"
        />
        <slot v-else name="action">
          <a :href="integrationAction">
            <Button
              size="sm"
              variant="faded"
              color="blue"
              :label="t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
            />
          </a>
        </slot>
      </div>
    </div>
  </SettingsSection>

  <Dialog
    ref="dialogRef"
    type="alert"
    :title="
      deleteConfirmationText.title ||
      t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.TITLE')
    "
    :description="
      deleteConfirmationText.message ||
      t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.MESSAGE')
    "
    :confirm-button-label="t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.YES')"
    :cancel-button-label="t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.NO')"
    @confirm="confirmDeletion"
  />
</template>
